import React, { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Panel,
  PanelHeader,
  PanelContent,
  Infobox,
} from "@flixbus/honeycomb-react";
import Step1Metadata from "../steps/Step1Metadata/Step1Metadata.component";
import Step2Subcategory from "../steps/Step2Subcategory/Step2Subcategory.component";
import Step3Dimension from "../steps/Step3Dimension/Step3Dimension.component";
import { wizardFooter } from "./WizardFooter";
import WizardProgress from "./WizardProgress.component";
import AiDraftPanel from "../AiDraftPanel.component";
import useAiChat from "../chat/useAiChat";
import {
  buildManualSubCategories,
  createCalculationSchema,
  defaultValues,
  isCreateDisabled,
  isNextDisabledOnMetadataStep,
  isNextDisabledOnSubcategoryStep,
  shouldShowAiDraftButton,
  panelId,
  type CreateCalculationFormData,
} from "../CreateCalculationPanel.constants";
import useCreateCalculationPanel from "../../../hooks/useCreateCalculationPanel";
import { isAiEnabled } from "../../../../../services/utils";
import shell from "../CreateCalculationPanel.module.scss";
import styles from "./Wizard.module.scss";

type StepIndex = 0 | 1 | 2;

export interface WizardProps {
  active: boolean;
  onClose: () => void;
  onSuccess?: (id: string) => void;
}

const Wizard: React.FC<WizardProps> = ({ active, onClose, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState<StepIndex>(0);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [mode, setMode] = useState<"wizard" | "aiDraft">("wizard");

  const form = useForm<CreateCalculationFormData>({
    resolver: yupResolver(createCalculationSchema),
    defaultValues,
    mode: "onTouched",
  });

  const supplierIdWatched =
    useWatch({ control: form.control, name: "supplierId" }) ?? "";
  const nameWatched = useWatch({ control: form.control, name: "name" }) ?? "";
  const kamNameWatched =
    useWatch({ control: form.control, name: "kamName" }) ?? "";
  const subCategoryIdsWatched =
    useWatch({ control: form.control, name: "subCategoryIds" }) ?? [];
  const dimensionTypeIdWatched =
    useWatch({ control: form.control, name: "dimensionTypeId" }) ?? null;
  const dimensionValuesWatched =
    useWatch({ control: form.control, name: "dimensionValues" }) ?? [];

  // Flag distinguishes "picked None" from "not yet touched" (both leave dimensionTypeId null).
  const [dimensionPicked, setDimensionPicked] = useState(false);

  const [subcategorySections, setSubcategorySections] = useState<string[]>([
    "",
  ]);

  const {
    calculableCategories,
    subCategoryOptions,
    dimensionTypeOptions,
    supplierOptions,
    refDataLoading,
    createCalculation,
    createLoading,
  } = useCreateCalculationPanel(active, {
    onSuccess,
    onClose,
    onError: () =>
      setSubmitError("Could not create the calculation. Try again."),
  });

  // The wizard "header" as labelled rows for the chat's opening scope card; built once >=1 sub is selected.
  const formValues = form.getValues();
  const supplierName = supplierOptions.find(
    (s) => s.id === supplierIdWatched,
  )?.name;
  const subCategoryNames = subCategoryIdsWatched
    .map((id) => subCategoryOptions.find((o) => o.id === id)?.name ?? id)
    .join(", ");
  const dimensionName =
    formValues.dimensionTypeId == null
      ? "None"
      : (dimensionTypeOptions.find((d) => d.id === formValues.dimensionTypeId)
          ?.name ?? "None");
  const dimensionValues = formValues.dimensionValues ?? [];
  const scopeFields =
    subCategoryIdsWatched.length > 0
      ? [
          { label: "Name", value: formValues.name },
          { label: "Line", value: String(formValues.line) },
          { label: "Planning year", value: String(formValues.planningYear) },
          { label: "Supplier", value: supplierName ?? "—" },
          { label: "KAM", value: formValues.kamName },
          { label: "Subcategories", value: subCategoryNames },
          {
            label: "Dimension",
            value:
              dimensionValues.length > 0
                ? `${dimensionName} (${dimensionValues.join(", ")})`
                : dimensionName,
          },
        ].filter((f) => f.value)
      : [];

  const aiChat = useAiChat(form, {
    scopeFields,
    onSuccess: (id) => {
      onClose();
      onSuccess?.(id);
    },
  });

  const subCategoryIdsByCategory = Object.fromEntries(
    calculableCategories.map((cat) => [
      cat.id,
      cat.subCategories.map((sub) => sub.id),
    ]),
  );

  const handleNext = async () => {
    const fields: (keyof CreateCalculationFormData)[] =
      currentStep === 0
        ? ["name", "kamName", "line", "planningYear", "supplierId"]
        : [];
    const valid = fields.length === 0 || (await form.trigger(fields));
    if (valid) {
      setCurrentStep((s) => (s < 2 ? ((s + 1) as StepIndex) : s));
    }
  };

  const handleBack = () => {
    setSubmitError(null);
    setCurrentStep((s) => (s > 0 ? ((s - 1) as StepIndex) : s));
  };

  const handleCreate = form.handleSubmit(
    async (data: CreateCalculationFormData) => {
      if (data.subCategoryIds.length === 0) return;
      setSubmitError(null);
      createCalculation({
        name: data.name,
        kamName: data.kamName,
        line: data.line,
        planningYear: data.planningYear,
        supplierId: data.supplierId,
        subCategories: buildManualSubCategories(data),
      });
    },
  );

  if (mode === "aiDraft" && subCategoryIdsWatched.length > 0) {
    return (
      <AiDraftPanel
        active={active}
        aiChat={aiChat}
        onBack={() => setMode("wizard")}
        onClose={onClose}
      />
    );
  }

  return (
    <Panel
      id={panelId}
      active={active}
      hasOverlay
      onOverlayClick={onClose}
      extraClasses={shell.panel}
    >
      <PanelHeader closeButtonProps={{ label: "Close", onClick: onClose }}>
        Create a calculation
      </PanelHeader>

      <PanelContent extraClasses={shell.panelContent}>
        <div className={styles.stepContent}>
          <WizardProgress currentStep={currentStep} />
          {currentStep === 0 && (
            <Step1Metadata form={form} suppliers={supplierOptions} />
          )}
          {currentStep === 0 &&
            !refDataLoading &&
            subCategoryOptions.length === 0 && (
              <Infobox appearance="danger" small>
                Reference data has no subcategories to calculate. Contact an
                admin to seed the catalog before creating a calculation.
              </Infobox>
            )}
          {currentStep === 1 && (
            <Step2Subcategory
              form={form}
              calculableCategories={calculableCategories}
              sections={subcategorySections}
              onSectionsChange={setSubcategorySections}
            />
          )}
          {currentStep === 2 && (
            <Step3Dimension
              form={form}
              dimensionTypeOptions={dimensionTypeOptions}
              hasPicked={dimensionPicked}
              onPick={() => setDimensionPicked(true)}
            />
          )}
          {submitError && (
            <Infobox appearance="danger" small>
              {submitError}
            </Infobox>
          )}
        </div>
      </PanelContent>

      {wizardFooter({
        currentStep,
        refDataLoading,
        createLoading,
        nextDisabled:
          (currentStep === 0 &&
            isNextDisabledOnMetadataStep({
              refDataLoading,
              name: nameWatched,
              kamName: kamNameWatched,
              supplierId: supplierIdWatched,
            })) ||
          (currentStep === 1 &&
            isNextDisabledOnSubcategoryStep({
              sections: subcategorySections,
              subCategoryIds: subCategoryIdsWatched,
              subCategoryIdsByCategory,
            })) ||
          false,
        createDisabled: isCreateDisabled({
          createLoading,
          refDataLoading,
          subCategoryIds: subCategoryIdsWatched,
          dimensionPicked,
          dimensionTypeId: dimensionTypeIdWatched,
          dimensionValues: dimensionValuesWatched,
        }),
        showAiDraft: shouldShowAiDraftButton({
          aiEnabled: isAiEnabled(),
          currentStep,
        }),
        onBack: handleBack,
        onNext: handleNext,
        onCreate: handleCreate,
        onDraftWithAi: () => setMode("aiDraft"),
      })}
    </Panel>
  );
};

export default Wizard;
